// InstallDesktopBanner — a slim, dismissible promo that surfaces only on
// Chromium-based desktops (Chrome/Edge/Brave) where the PWA `beforeinstall
// prompt` is supported. Listens for the prompt, stashes it, and exposes
// "Install desktop app" → triggers the native install UI from a user gesture.
//
// Auto-hides when:
//   • the app is already installed (`display-mode: standalone` media query),
//   • the user is on a mobile device (covered by the Capacitor app),
//   • the user dismissed it (localStorage flag).

import React, { useEffect, useState, useCallback } from "react";
import { Download, X, Monitor } from "lucide-react";
import { isMobileApp } from "../lib/platform";

const DISMISSED_KEY = "opencsv:install-banner-dismissed-v1";

export default function InstallDesktopBanner() {
    const [promptEvent, setPromptEvent] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISSED_KEY) === "1"; } catch { return false; }
    });

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        if (isMobileApp()) return undefined; // not relevant inside Capacitor shell

        // Detect "already installed" via the standalone media query.
        const mql = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
        if (mql?.matches) setInstalled(true);
        const onChange = (e) => setInstalled(e.matches);
        mql?.addEventListener?.("change", onChange);

        const onBeforeInstall = (e) => {
            // Stop Chrome's automatic mini-infobar; we'll show our own UI.
            e.preventDefault();
            setPromptEvent(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setPromptEvent(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
            mql?.removeEventListener?.("change", onChange);
        };
    }, []);

    const handleInstall = useCallback(async () => {
        if (!promptEvent) return;
        try {
            await promptEvent.prompt();
            const choice = await promptEvent.userChoice;
            if (choice && choice.outcome === "accepted") {
                setInstalled(true);
            }
        } catch {/* user cancelled or browser quirk */ }
        finally { setPromptEvent(null); }
    }, [promptEvent]);

    const handleDismiss = () => {
        setDismissed(true);
        try { localStorage.setItem(DISMISSED_KEY, "1"); } catch {/* private mode */ }
    };

    if (installed || dismissed || !promptEvent || isMobileApp()) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-40"
            data-testid="install-desktop-banner"
        >
            <div className="bg-white border border-ink shadow-brutal px-4 py-3 flex items-start gap-3">
                <div className="hidden sm:flex w-9 h-9 items-center justify-center border border-cobalt text-cobalt flex-shrink-0">
                    <Monitor size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="ui-heading text-[10px] uppercase tracking-widest text-ink-muted">/ desktop app</div>
                    <p className="font-display font-black tracking-tighter text-base leading-tight mt-0.5">
                        Make CSVs open in OpenCSV.
                    </p>
                    <p className="text-xs text-ink-soft mt-1">
                        Install OpenCSV as a desktop app and it'll show up in your right-click "Open with…" menu for every CSV.
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                        <button
                            onClick={handleInstall}
                            className="btn-brutal bg-cobalt text-white border border-cobalt px-3 py-1.5 ui-heading text-xs font-semibold inline-flex items-center gap-1.5"
                            data-testid="install-desktop-app-button"
                        >
                            <Download size={12} /> Install OpenCSV
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="ui-heading text-[11px] uppercase tracking-widest text-ink-muted px-2"
                            data-testid="install-banner-dismiss"
                        >
                            Not now
                        </button>
                    </div>
                </div>
                <button
                    onClick={handleDismiss}
                    aria-label="Dismiss"
                    className="text-ink-muted hover:text-ink p-1 -mt-1 -mr-1"
                    data-testid="install-banner-close"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
